import { HubAuthProvider, useHubAuth } from "./useHubAuth";
import { AuthGate, HubBoot } from "./AuthGate";

/**
 * The entry every suite application mounts before its own UI: the shared
 * session is resolved, and while there is none the Ortam sign-in screen stands
 * in front of the application. One gate, one account, every app — wiring is
 * one line in the application entry (`<HubGateRoot appId="kor" …>`).
 */

interface HubGateRootProps {
  /** The suite register id of this application (config/suite.json → apps[].id). */
  appId: string;
  /** Human name shown on the sign-in card. */
  appName: string;
  children: React.ReactNode;
}

function GateBody({ appId, appName, children }: Omit<HubGateRootProps, "children"> & { children: React.ReactNode }) {
  // Sign-out travels through `useHubAuth().logout()` — the suite top bar's
  // `chrome:logout` event is wired to it inside the provider.
  const { user, booting, unreachable, login, register, retry } = useHubAuth();

  if (booting) return <HubBoot />;
  if (!user) {
    return (
      <AuthGate
        onLogin={login}
        onRegister={register}
        unreachable={unreachable}
        onRetry={retry}
        appId={appId}
        appName={appName}
      />
    );
  }
  return <>{children}</>;
}

export function HubGateRoot({ appId, appName, children }: HubGateRootProps) {
  return (
    <HubAuthProvider>
      <GateBody appId={appId} appName={appName}>
        {children}
      </GateBody>
    </HubAuthProvider>
  );
}
